import React, { useState, useEffect, useMemo } from 'react';
import { UserX, PhoneCall, Mail, Archive, RotateCcw, Filter, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RelanceTab = ({ token }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noshowStats, setNoshowStats] = useState(null);
  const [filter, setFilter] = useState('all'); // all, noshow, responded
  const [archiving, setArchiving] = useState(null);

  const fetchData = async () => {
    try {
      const [relRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/relance`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/stats/no-show`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (relRes.ok) {
        const data = await relRes.json();
        setCandidates(data.candidates || []);
      }
      if (statsRes.ok) {
        setNoshowStats(await statsRes.json());
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  const markNoshow = async (bookingId) => {
    const res = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/mark-noshow`, {
      method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) { toast.success('Marqué no-show'); fetchData(); }
    else toast.error('Erreur');
  };

  const unmarkNoshow = async (bookingId) => {
    const res = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/unmark-noshow`, {
      method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) { toast.success('No-show retiré'); fetchData(); }
    else toast.error('Erreur');
  };

  const archiveCandidate = async (bookingId, reason) => {
    setArchiving(bookingId);
    const res = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/archive`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason })
    });
    if (res.ok) {
      toast.success('Candidat archivé');
      setCandidates(prev => prev.filter(c => c.bookingId !== bookingId));
    } else toast.error('Erreur archivage');
    setArchiving(null);
  };

  const filtered = useMemo(() => {
    if (filter === 'noshow') return candidates.filter(c => c.noshow);
    if (filter === 'confirmed') return candidates.filter(c => c.confirmed_by_candidate);
    return candidates;
  }, [candidates, filter]);

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>Chargement...</div>;

  return (
    <div data-testid="relance-tab" style={{ maxWidth: '1100px', margin: '0 auto' }}>
      {/* No-show stats */}
      {noshowStats && (
        <div data-testid="noshow-stats" style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem'
        }}>
          <StatCard label="Total RDV passés" value={noshowStats.total_past} color="#64748b" />
          <StatCard label="Honorés" value={noshowStats.honored} color="#22c55e" />
          <StatCard label="No-show" value={noshowStats.noshow} subtext={`${noshowStats.noshow_rate}%`} color="#ef4444" />
          <StatCard label="Annulés" value={noshowStats.cancelled} subtext={`${noshowStats.cancel_rate}%`} color="#f59e0b" />
          <StatCard label="Confirmés" value={noshowStats.confirmed_by_candidate} subtext={`${noshowStats.confirmation_rate}%`} color="#3b82f6" />
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 700 }}>
            Candidats à relancer
          </h3>
          <p style={{ margin: '0.25rem 0 0', color: '#999', fontSize: '0.8rem' }}>
            RDV découverte passés depuis 48h+ sans achat
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['all', 'noshow', 'confirmed'].map(f => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              style={{
                padding: '0.4rem 0.8rem', borderRadius: '1rem', border: '1px solid',
                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                background: filter === f ? (f === 'noshow' ? '#fef2f2' : f === 'confirmed' ? '#f0fdf4' : '#f1f5f9') : 'white',
                color: filter === f ? (f === 'noshow' ? '#ef4444' : f === 'confirmed' ? '#22c55e' : '#475569') : '#999',
                borderColor: filter === f ? (f === 'noshow' ? '#fca5a5' : f === 'confirmed' ? '#86efac' : '#cbd5e1') : '#e5e7eb'
              }}
            >
              {f === 'all' ? `Tous (${candidates.length})` : f === 'noshow' ? `No-show (${candidates.filter(c => c.noshow).length})` : `Confirmés (${candidates.filter(c => c.confirmed_by_candidate).length})`}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#999', background: 'var(--bg-card)', borderRadius: '1rem' }}>
          <PhoneCall size={32} style={{ marginBottom: '0.5rem', opacity: 0.4 }} />
          <p>Aucun candidat à relancer pour le moment</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(c => (
            <CandidateCard
              key={c.bookingId}
              candidate={c}
              onMarkNoshow={markNoshow}
              onUnmarkNoshow={unmarkNoshow}
              onArchive={archiveCandidate}
              archiving={archiving}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, subtext, color }) => (
  <div style={{
    background: 'var(--bg-card)', borderRadius: '0.75rem', padding: '1rem 1.25rem',
    border: '1px solid var(--border-light)', textAlign: 'center'
  }}>
    <div style={{ fontSize: '1.5rem', fontWeight: 800, color }}>{value}</div>
    {subtext && <div style={{ fontSize: '0.75rem', fontWeight: 600, color, opacity: 0.7 }}>{subtext}</div>}
    <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '0.25rem' }}>{label}</div>
  </div>
);

const CandidateCard = ({ candidate: c, onMarkNoshow, onUnmarkNoshow, onArchive, archiving }) => {
  const [showArchive, setShowArchive] = useState(false);

  const daysSince = c.days_since_rdv;
  const urgency = daysSince <= 3 ? '#22c55e' : daysSince <= 7 ? '#f59e0b' : '#ef4444';

  return (
    <div data-testid={`relance-card-${c.bookingId}`} style={{
      background: 'var(--bg-card)', borderRadius: '0.75rem', padding: '1rem 1.25rem',
      border: `1px solid ${c.noshow ? '#fca5a5' : 'var(--border-light)'}`,
      display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
      opacity: archiving === c.bookingId ? 0.5 : 1,
      transition: 'all 0.2s'
    }}>
      {/* Avatar */}
      <div style={{
        width: 40, height: 40, borderRadius: '50%', background: c.noshow ? '#fef2f2' : '#f0fdf4',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        {c.noshow ? <UserX size={18} style={{ color: '#ef4444' }} /> : <PhoneCall size={18} style={{ color: '#22c55e' }} />}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: '200px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
          {c.prenom} {c.nom}
          {c.noshow && <span style={{ marginLeft: '0.5rem', padding: '1px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700, background: '#fef2f2', color: '#ef4444' }}>NO-SHOW</span>}
          {c.confirmed_by_candidate && <span style={{ marginLeft: '0.5rem', padding: '1px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700, background: '#f0fdf4', color: '#22c55e' }}>CONFIRMÉ</span>}
        </div>
        <div style={{ fontSize: '0.8rem', color: '#666', display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.2rem' }}>
          {c.email && <a href={`mailto:${c.email}`} style={{ color: '#E75480', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Mail size={12} />{c.email}</a>}
          {c.telephone && <a href={`tel:${c.telephone}`} style={{ color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><PhoneCall size={12} />{c.telephone}</a>}
        </div>
        <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '0.2rem' }}>
          {c.programName || c.programId} — RDV du {new Date(c.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} à {c.time}
        </div>
      </div>

      {/* Days since */}
      <div style={{ textAlign: 'center', flexShrink: 0 }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: urgency }}>{Math.round(daysSince)}j</div>
        <div style={{ fontSize: '0.6rem', color: '#999' }}>depuis RDV</div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flexShrink: 0 }}>
        {!c.noshow ? (
          <button
            data-testid={`mark-noshow-${c.bookingId}`}
            onClick={() => onMarkNoshow(c.bookingId)}
            style={{
              padding: '0.3rem 0.6rem', borderRadius: '0.4rem', border: '1px solid #fca5a5',
              background: 'white', color: '#ef4444', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600
            }}
          >No-show</button>
        ) : (
          <button
            data-testid={`unmark-noshow-${c.bookingId}`}
            onClick={() => onUnmarkNoshow(c.bookingId)}
            style={{
              padding: '0.3rem 0.6rem', borderRadius: '0.4rem', border: '1px solid #86efac',
              background: 'white', color: '#22c55e', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600
            }}
          ><RotateCcw size={10} style={{ marginRight: '2px' }} />Retirer</button>
        )}

        {!showArchive ? (
          <button
            data-testid={`archive-btn-${c.bookingId}`}
            onClick={() => setShowArchive(true)}
            style={{
              padding: '0.3rem 0.6rem', borderRadius: '0.4rem', border: '1px solid #d1d5db',
              background: 'white', color: '#6b7280', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: '3px'
            }}
          ><Archive size={10} />Archiver</button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <button
              data-testid={`archive-notinterested-${c.bookingId}`}
              onClick={() => onArchive(c.bookingId, 'non_interesse')}
              style={{ padding: '0.2rem 0.5rem', borderRadius: '0.3rem', border: '1px solid #fca5a5', background: '#fef2f2', color: '#ef4444', cursor: 'pointer', fontSize: '0.6rem', fontWeight: 600 }}
            >Pas intéressé</button>
            <button
              data-testid={`archive-noresponse-${c.bookingId}`}
              onClick={() => onArchive(c.bookingId, 'ne_repond_plus')}
              style={{ padding: '0.2rem 0.5rem', borderRadius: '0.3rem', border: '1px solid #fde68a', background: '#fefce8', color: '#b45309', cursor: 'pointer', fontSize: '0.6rem', fontWeight: 600 }}
            >Ne répond plus</button>
            <button
              onClick={() => setShowArchive(false)}
              style={{ padding: '0.2rem 0.5rem', borderRadius: '0.3rem', border: '1px solid #e5e7eb', background: 'white', color: '#999', cursor: 'pointer', fontSize: '0.6rem' }}
            >Annuler</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RelanceTab;
